// src/hooks/useCopyToClipboard.ts
// Reusable hook for copy-to-clipboard functionality with toast feedback
import { useState } from 'react';

interface CopyToClipboardResult {
  showToast: boolean;
  toastPosition: { x: number; y: number } | null;
  handleCopy: (text: string, event: React.MouseEvent) => void;
  hideToast: () => void;
}

/**
 * Custom hook for copy-to-clipboard functionality with EventToast feedback
 * Handles both modern clipboard API and fallback for older browsers/mobile
 */
export const useCopyToClipboard = (): CopyToClipboardResult => {
  const [showToast, setShowToast] = useState(false);
  const [toastPosition, setToastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  /**
   * Fallback copy method for older browsers or non-HTTPS contexts
   */
  const fallbackCopyTextToClipboard = (text: string, x: number, y: number) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setToastPosition({ x, y });
        setShowToast(true);
      } else {
        console.error('Fallback: Copy command was unsuccessful');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }

    document.body.removeChild(textArea);
  };

  /**
   * Main copy handler with EventToast and mobile support
   */
  const handleCopy = (text: string, event: React.MouseEvent) => {
    event.stopPropagation();

    // Get click position for toast
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    // Copy to clipboard with fallback for mobile/older browsers
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setToastPosition({ x, y });
          setShowToast(true);
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
          fallbackCopyTextToClipboard(text, x, y);
        });
    } else {
      fallbackCopyTextToClipboard(text, x, y);
    }
  };

  const hideToast = () => {
    setShowToast(false);
  };

  return {
    showToast,
    toastPosition,
    handleCopy,
    hideToast,
  };
};
